// Admin > Audit Logs: every action in the application — who did what, when, on whose behalf,
// and what changed (field values before and after). The Messages tab lists every WhatsApp, SMS
// and email the app sent or tried to send (components/MessageLogsPanel.js).
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Typography, Paper, Stack, TextField, MenuItem, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Drawer, IconButton, Divider, Alert, LinearProgress, InputAdornment, Tooltip, Tabs, Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { AUDIT_LOGS_QUERY, AUDIT_LOG_FACETS_QUERY } from '../graphql/queries';
import MessageLogsPanel from '../components/MessageLogsPanel';

const ACTION = {
  api: { label: 'Action', color: 'primary' }, create: { label: 'Created', color: 'success' }, update: { label: 'Changed', color: 'info' },
  delete: { label: 'Deleted', color: 'error' }, upload: { label: 'Upload', color: 'secondary' }, download: { label: 'Download', color: 'default' },
  view: { label: 'Viewed', color: 'default' }, rsvp: { label: 'RSVP', color: 'warning' },
};
const EMPTY = { from: '', to: '', action: '', entityType: '', success: '', search: '', requestId: '' };
const fmt = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
const show = (v) => (v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
// "createVendorSelection" → "Create vendor selection"
const humanize = (s) => (s ? s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase()).replace(/ ([A-Z])/g, (m, c) => ` ${c.toLowerCase()}`) : '');

function whoOf(r) {
  if (r.actorRole === 'system') return 'System (background job)';
  return r.actorEmail || (r.action === 'view' || r.action === 'rsvp' ? 'Guest (invitation link)' : 'Not signed in');
}
function whatOf(r) {
  if (r.action === 'api') return humanize(r.operation);
  return [r.entityType, r.operation && r.action !== 'view' && r.action !== 'rsvp' ? `via ${humanize(r.operation)}` : humanize(r.operation)].filter(Boolean).join(' · ');
}

// Field-by-field table of a change (or the values of a created/deleted record)
function ChangesTable({ log }) {
  const c = log.changes;
  if (!c || typeof c !== 'object' || !Object.keys(c).length) return null;
  const isDiff = Object.values(c).every(v => v && typeof v === 'object' && ('to' in v || 'from' in v));
  return (
    <Table size="small" sx={{ mb: 2 }}>
      <TableHead><TableRow>
        <TableCell>Field</TableCell>
        {isDiff ? <><TableCell>Before</TableCell><TableCell>After</TableCell></> : <TableCell>Value</TableCell>}
      </TableRow></TableHead>
      <TableBody>
        {Object.entries(c).map(([k, v]) => (
          <TableRow key={k}>
            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{k}</TableCell>
            {isDiff ? (
              <>
                <TableCell sx={{ color: 'error.main', wordBreak: 'break-word' }}>{show(v.from)}</TableCell>
                <TableCell sx={{ color: 'success.main', wordBreak: 'break-word' }}>{show(v.to)}</TableCell>
              </>
            ) : <TableCell sx={{ wordBreak: 'break-word' }}>{show(v)}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function toCsv(items) {
  const cols = ['occurredAt', 'actorEmail', 'actorRole', 'actingForEmail', 'action', 'operation', 'entityType', 'entityId', 'success', 'error', 'ipAddress', 'changes', 'details', 'requestId'];
  const cell = (v) => { const s = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v); return `"${s.replace(/"/g, '""')}"`; };
  return [cols.join(','), ...items.map(r => cols.map(c => cell(r[c])).join(','))].join('\n');
}

export default function AdminAuditLogsPage() {
  const [tab, setTab] = useState('activity');
  const tabs = (
    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Tab value="activity" label="Activity" />
      <Tab value="messages" label="Messages (WhatsApp · SMS · Email)" />
    </Tabs>
  );
  if (tab === 'messages') {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700}>Audit Logs</Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Every WhatsApp, SMS and email the app sent or tried to send. Passwords and reset links are hidden.
        </Typography>
        {tabs}
        <MessageLogsPanel />
      </Box>
    );
  }
  return <ActivityLog tabs={tabs} />;
}

function ActivityLog({ tabs }) {
  const [filter, setFilter] = useState(EMPTY);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [open, setOpen] = useState(null);

  const vars = {
    filter: {
      ...(filter.from && { from: filter.from }), ...(filter.to && { to: filter.to }),
      ...(filter.action && { action: filter.action }), ...(filter.entityType && { entityType: filter.entityType }),
      ...(filter.success !== '' && { success: filter.success === 'ok' }), ...(filter.search.trim() && { search: filter.search.trim() }),
      ...(filter.requestId && { requestId: filter.requestId }),
    },
    limit: rowsPerPage, offset: page * rowsPerPage,
  };
  const { data, loading, error, refetch } = useQuery(AUDIT_LOGS_QUERY, { variables: vars, fetchPolicy: 'cache-and-network' });
  const { data: facetData } = useQuery(AUDIT_LOG_FACETS_QUERY, { fetchPolicy: 'cache-and-network' });
  const items = data?.auditLogs?.items || [];
  const total = data?.auditLogs?.total || 0;
  const facets = facetData?.auditLogFacets || { actions: [], entityTypes: [] };
  const set = (k) => (e) => { setFilter(f => ({ ...f, [k]: e.target.value })); setPage(0); };

  const exportCsv = () => {
    const blob = new Blob([toCsv(items)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Audit Logs</Typography>
          <Typography variant="body2" color="text.secondary">
            Every action in the application: who did it, when, on whose behalf, and what changed. Passwords, tokens and signatures are never recorded.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => refetch()}>Refresh</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!items.length} onClick={exportCsv}>Export page (CSV)</Button>
        </Stack>
      </Stack>
      {tabs}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField size="small" placeholder="Search who, action, record, value…" value={filter.search} onChange={set('search')} sx={{ flex: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField size="small" type="date" label="From" value={filter.from} onChange={set('from')} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="To" value={filter.to} onChange={set('to')} InputLabelProps={{ shrink: true }} />
          <TextField size="small" select label="Type" value={filter.action} onChange={set('action')} sx={{ minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>
            {facets.actions.map(a => <MenuItem key={a} value={a}>{ACTION[a]?.label || a}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Record" value={filter.entityType} onChange={set('entityType')} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            {facets.entityTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Result" value={filter.success} onChange={set('success')} sx={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ok">Succeeded</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </TextField>
          <Tooltip title="Clear filters"><IconButton onClick={() => { setFilter(EMPTY); setPage(0); }}><FilterAltOffIcon /></IconButton></Tooltip>
        </Stack>
        {filter.requestId && (
          <Chip sx={{ mt: 1.5 }} label="Showing one request — everything it did" onDelete={() => setFilter(f => ({ ...f, requestId: '' }))} />
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {loading && <LinearProgress />}
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Type</TableCell><TableCell>What</TableCell><TableCell>Result</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {items.map(r => {
              const a = ACTION[r.action] || { label: r.action, color: 'default' };
              return (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(r)}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(r.occurredAt)}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="body2">{whoOf(r)}</Typography>
                    {(r.actorRole && r.actorRole !== 'system') && <Typography variant="caption" color="text.secondary">{r.actorRole}</Typography>}
                    {r.actingForEmail && <Typography variant="caption" color="primary.main" display="block">for {r.actingForEmail}</Typography>}
                  </TableCell>
                  <TableCell><Chip size="small" color={a.color} variant={r.action === 'api' ? 'filled' : 'outlined'} label={a.label} /></TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Typography variant="body2">{whatOf(r)}</Typography>
                    {r.entityId && <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.entityId}</Typography>}
                  </TableCell>
                  <TableCell>
                    {r.success ? <Chip size="small" color="success" variant="outlined" label="OK" />
                      : <Tooltip title={r.error || ''}><Chip size="small" color="error" label="Failed" /></Tooltip>}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && !items.length && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No audit entries match these filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[25, 50, 100, 250]}
          onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Drawer anchor="right" open={Boolean(open)} onClose={() => setOpen(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
        {open && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" fontWeight={700}>{whatOf(open) || (ACTION[open.action]?.label || open.action)}</Typography>
              <IconButton onClick={() => setOpen(null)}><CloseIcon /></IconButton>
            </Stack>
            <Stack spacing={0.5} mb={2}>
              {[
                ['When', fmt(open.occurredAt)],
                ['Who', `${whoOf(open)}${open.actorRole && open.actorRole !== 'system' ? ` (${open.actorRole})` : ''}`],
                ['Acting for', open.actingForEmail],
                ['Type', ACTION[open.action]?.label || open.action],
                ['Action', humanize(open.operation)],
                ['Record', [open.entityType, open.entityId].filter(Boolean).join(' · ')],
                ['Result', open.success ? 'Succeeded' : `Failed — ${open.error || ''}`],
                ['IP address', open.ipAddress],
                ['Browser', open.userAgent],
              ].filter(([, v]) => v).map(([k, v]) => (
                <Stack key={k} direction="row" spacing={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>{k}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{v}</Typography>
                </Stack>
              ))}
            </Stack>
            {open.requestId && (
              <Button size="small" variant="outlined" sx={{ mb: 2 }}
                onClick={() => { setFilter({ ...EMPTY, requestId: open.requestId }); setPage(0); setOpen(null); }}>
                Show everything this request did
              </Button>
            )}
            <Divider sx={{ mb: 2 }} />
            {open.changes && (
              <>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>{open.action === 'update' ? 'What changed' : 'Values'}</Typography>
                <ChangesTable log={open} />
              </>
            )}
            {open.details && (
              <>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>{open.action === 'api' ? 'Inputs' : 'Details'}</Typography>
                <Box component="pre" sx={{ bgcolor: '#f7f7f9', p: 1.5, borderRadius: 1, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(open.details, null, 2)}
                </Box>
              </>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
